import React from "react";

/**
 * Comprehensive CPG category list, grouped logically. When the user picks
 * "Other (specify)" we swap the select for a free-text input so they can
 * type whatever niche they're in. The stored value is always a plain string
 * (the same shape the LogoIn backend schema expects), so nothing downstream
 * changes.
 */
export const CPG_CATEGORY_GROUPS = [
  {
    label: "Food",
    items: [
      "Snacks", "Bars / bites", "Frozen meals", "Ready meals",
      "Bakery / pastries", "Confectionery / chocolate", "Condiments / sauces",
      "Pantry staples", "Plant-based meat / dairy", "Cereal / breakfast",
      "Better-for-you kids food",
    ],
  },
  {
    label: "Beverage",
    items: [
      "Functional drinks", "Sparkling / soft drinks", "Water / enhanced water",
      "Coffee / cold brew", "Tea / kombucha", "Juice / smoothie",
      "Energy drinks", "Alcohol · beer / seltzer", "Alcohol · wine",
      "Alcohol · spirits / RTD", "Non-alc spirits", "Protein / recovery drinks",
    ],
  },
  {
    label: "Wellness & Beauty",
    items: [
      "Supplements / nutraceuticals", "Vitamins / gummies",
      "Sports nutrition / protein", "Beauty / skincare",
      "Haircare", "Bodycare", "Fragrance", "Colour cosmetics",
      "Oral care", "Feminine care", "Sexual wellness", "Sleep / stress",
    ],
  },
  {
    label: "Home & Personal",
    items: [
      "Household cleaning", "Laundry", "Air / home fragrance",
      "Kitchenware / cookware", "Pantry organization",
      "Personal-care disposables",
    ],
  },
  {
    label: "Lifestyle & Other",
    items: [
      "Pet food / treats", "Pet supplements",
      "Baby food / formula", "Baby / kids personal care",
      "Cannabis / CBD", "Nicotine alternatives",
      "Outdoor / adventure",
    ],
  },
];

// Flatten for quick membership checks.
const ALL_KNOWN = new Set(
  CPG_CATEGORY_GROUPS.flatMap((g) => g.items),
);

const OTHER_KEY = "__other__";

export default function CategoryPicker({ value, onChange, testid }) {
  const isOther = !!value && !ALL_KNOWN.has(value);
  const [customMode, setCustomMode] = React.useState(isOther);
  const [customText, setCustomText] = React.useState(isOther ? value : "");

  // Keep custom text in sync when parent value changes externally.
  React.useEffect(() => {
    const other = !!value && !ALL_KNOWN.has(value);
    setCustomMode(other);
    if (other) setCustomText(value);
  }, [value]);

  const onSelectChange = (e) => {
    const v = e.target.value;
    if (v === OTHER_KEY) {
      setCustomMode(true);
      // Preserve existing custom text if we're re-picking Other
      onChange(customText || "");
    } else {
      setCustomMode(false);
      onChange(v);
    }
  };

  const selectValue = customMode ? OTHER_KEY : (value || "");

  return (
    <div className="grid gap-2" data-testid={testid}>
      <select
        value={selectValue}
        onChange={onSelectChange}
        data-testid={`${testid}-select`}
        className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
      >
        <option value="">Pick a category…</option>
        {CPG_CATEGORY_GROUPS.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.items.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </optgroup>
        ))}
        <option value={OTHER_KEY}>Other (type your own…)</option>
      </select>
      {customMode && (
        <input
          type="text"
          value={customText}
          onChange={(e) => { setCustomText(e.target.value); onChange(e.target.value); }}
          placeholder="Type your specific category (e.g. Ceremonial-grade matcha)"
          maxLength={120}
          autoFocus
          data-testid={`${testid}-custom`}
          className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
      )}
    </div>
  );
}
